import { ConversationApi, CustomerApi, InboxApi } from 'conversations/api'
import qs from 'query-string'
import { UserPreference } from 'shared/src/store/actions/user'
import { Query } from 'shared/src/typings/conversations/query'
import { Availability } from 'shared/typings/user/Availability'
import { User } from 'shared/typings/user/User'
import { UserStatus } from 'shared/typings/user/UserStatus'
import spacetime from 'spacetime'

interface ResultResponse {
  result: string
  ok: boolean
}

export const addOrgUserToConversation = (
  conversationId: number,
  orgUserId: number
): Promise<void> => {
  return ConversationApi.post(`/conversations/${conversationId}/participants`, {
    participants: [orgUserId],
  })
}

export const addTeamToConversation = (conversationId: number, teamId: number): Promise<void> => {
  return ConversationApi.post(`/conversations/${conversationId}/participants`, {
    teams: [teamId],
  })
}

export const closeAndLeaveConversation = async (
  conversationId: number
): Promise<ResultResponse> => {
  const res = await InboxApi.patch(`/conversations/${conversationId}/closeAndLeave`)
  return res.data
}

export const removeOrgUserFromConversation = async (
  conversationId: number,
  orgUserId: number
): Promise<void> => {
  return ConversationApi.delete(`/conversations/${conversationId}/participants`, {
    data: {
      participants: [orgUserId],
    },
  })
}

export const fetchUsers = async (orgId: number): Promise<User[]> => {
  const res = await CustomerApi.get<User[]>(`/organizations/${orgId}/users`, {
    params: {
      status: [UserStatus.INVITED, UserStatus.ENABLED, UserStatus.ARCHIVED],
    },
    paramsSerializer: (params) => {
      return qs.stringify(params)
    },
  })
  return res.data
}

export const fetchPreferences = async (): Promise<any> => {
  const res = await InboxApi.get<any>('/user/preferences')
  return res.data
}

export const sendActiveStatus = async (
  source: string,
  conversationId: number | undefined
): Promise<number> => {
  const params = {
    isActive: true,
    source,
    conversationId,
  }
  const res = await ConversationApi.post(`/agents/active?${qs.stringify(params)}`)
  return res.data
}

export const patchAgentAvailabilityWithDelay = async (
  userId: number,
  delayInSeconds: number
): Promise<boolean> => {
  const delay = {
    nextAvailableTime: spacetime.now().epoch + delayInSeconds,
  }
  const resp = await CustomerApi.patch<boolean>(`/users/${userId}/availability/delay`, delay)
  return resp.data
}

export const patchIndefinteAway = async (userId: number): Promise<any> => {
  const operation = [{ operation: 'delete', property: 'agent_availability_delay' }]
  return CustomerApi.patch<User>(`/users/${userId}/properties2`, operation)
}

interface PatchOperation {
  operation: string
  property: string
  value: any
}
export const patchUserProperties = (
  userId: number,
  patchOperation: PatchOperation[]
): Promise<any> => CustomerApi.patch(`/users/${userId}/properties2`, patchOperation)

export const patchAgentAvailability = async (
  userId: number,
  availability: Availability | null
): Promise<Availability> => {
  const operation = [{ operation: 'set', property: 'availability', value: availability }]
  const resp = await CustomerApi.patch<User>(`/users/${userId}/patch`, operation)
  return resp.data.availability
}

export const updateUserPreference = async (
  preference: UserPreference,
  value: boolean | string | number | object | undefined,
  userId?: number
) => {
  await InboxApi.patch('/user/preferences', {
    userId: userId,
    name: preference,
    value,
  })
}

interface HideAllPayload extends Omit<Query, 'participantIds'> {
  participantIds: number[]
}

export const hideAllConversations = async (query: HideAllPayload): Promise<void> => {
  const res = await InboxApi.post('conversations/hideAll', query)
  return res.data
}
